// ──────────────────────────────────────────────────────────────────────────────
// Fonte ÚNICA do "resumo dos filtros" — usada nos chips do topo da lista E no
// cabeçalho do relatório, pra os dois ficarem sempre iguais.
// ──────────────────────────────────────────────────────────────────────────────

export interface ValoresFiltros {
  searchTerm: string;
  status: string;
  servidor: string;
  sexo: string;
  zonaRural: string;
  idadeMin: string;
  idadeMax: string;
  profissao: string;
  bairro: string;
  cadastroInicio: string;
  cadastroFim: string;
}

/** Valores padrão (estado inicial dos filtros) */
export const FILTROS_PADRAO: ValoresFiltros = {
  searchTerm: "",
  status: "ativos",
  servidor: "todos",
  sexo: "todos",
  zonaRural: "todas",
  idadeMin: "",
  idadeMax: "",
  profissao: "",
  bairro: "",
  cadastroInicio: "",
  cadastroFim: "",
};

const LABEL_STATUS: Record<string, string> = {
  ativos: "Ativos",
  desativados: "Desativados",
  todos: "Todos",
};
const LABEL_SERVIDOR: Record<string, string> = {
  servidor: "Servidor",
  "nao-servidor": "Não servidor",
};
const LABEL_SEXO: Record<string, string> = { M: "Masculino", F: "Feminino" };
const LABEL_ZONA: Record<string, string> = { rural: "Zona rural", urbana: "Zona urbana" };

const dataBR = (s: string) => {
  const d = new Date(s + "T00:00:00");
  return isNaN(d.getTime()) ? s : d.toLocaleDateString("pt-BR");
};

/** Descreve um filtro ativo (para chip removível) */
export interface FiltroAtivo {
  id: string;
  texto: string;
  /** o que aplicar sobre os filtros para "remover" este (relaxar) */
  reset: Partial<ValoresFiltros>;
}

/**
 * Monta a lista de filtros ATIVOS (ignora os que estão no padrão/"todos").
 * Cada item traz o texto legível e o `reset` para removê-lo individualmente.
 */
export function montarFiltrosAtivos(v: ValoresFiltros): FiltroAtivo[] {
  const out: FiltroAtivo[] = [];

  if (v.searchTerm)
    out.push({ id: "busca", texto: `Busca: "${v.searchTerm}"`, reset: { searchTerm: "" } });

  if (v.status && v.status !== "todos")
    out.push({
      id: "status",
      texto: `Status: ${LABEL_STATUS[v.status] ?? v.status}`,
      reset: { status: "todos" },
    });

  if (v.servidor && v.servidor !== "todos")
    out.push({
      id: "servidor",
      texto: `Servidor: ${LABEL_SERVIDOR[v.servidor] ?? v.servidor}`,
      reset: { servidor: "todos" },
    });

  if (v.sexo && v.sexo !== "todos")
    out.push({
      id: "sexo",
      texto: `Gênero: ${LABEL_SEXO[v.sexo] ?? v.sexo}`,
      reset: { sexo: "todos" },
    });

  if (v.zonaRural && v.zonaRural !== "todas")
    out.push({
      id: "zona",
      texto: `Localidade: ${LABEL_ZONA[v.zonaRural] ?? v.zonaRural}`,
      reset: { zonaRural: "todas" },
    });

  if (v.profissao)
    out.push({ id: "profissao", texto: `Profissão: ${v.profissao}`, reset: { profissao: "" } });

  if (v.bairro) out.push({ id: "bairro", texto: `Bairro: ${v.bairro}`, reset: { bairro: "" } });

  if (v.idadeMin || v.idadeMax)
    out.push({
      id: "idade",
      texto: `Idade: ${v.idadeMin || "0"}–${v.idadeMax || "∞"}`,
      reset: { idadeMin: "", idadeMax: "" },
    });

  if (v.cadastroInicio || v.cadastroFim)
    out.push({
      id: "cadastro",
      texto: `Cadastro: ${v.cadastroInicio ? dataBR(v.cadastroInicio) : "início"} a ${
        v.cadastroFim ? dataBR(v.cadastroFim) : "hoje"
      }`,
      reset: { cadastroInicio: "", cadastroFim: "" },
    });

  return out;
}

/** Só os textos (para o cabeçalho do relatório) */
export function resumoFiltrosTexto(v: ValoresFiltros): string[] {
  return montarFiltrosAtivos(v).map((f) => f.texto);
}
